import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import CourseCard from "../CoursesDisplay/CourseCard";
import { preDecodeMany } from "Utilities/imageDecodeCache";
import { getPlaceholderGradient } from "Utilities/placeholderColor";
import { useSlotWidth } from "./useSlotWidth";
import { useShelfMount } from "./useShelfMount";
import ShelfSkeleton from "./ShelfSkeleton";
import ShelfHeader from "./ShelfHeader";

const OVERSCAN = 8;

const Shelf = ({
  title,
  icon,
  items = [],
  progressMap = null,
  onSeeAll,
  lazyPaint = true,
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const slotWidth = useSlotWidth();
  const scrollRef = useRef(null);
  const { sectionRef, isMounted } = useShelfMount(lazyPaint);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => slotWidth,
    horizontal: true,
    overscan: OVERSCAN,
  });

  // Pre-decode all thumbnails once. The module-scope LRU cache keeps the
  // decoded bitmaps pinned, so re-mounted <img> elements skip the
  // decode-flash ("checker-imaging") on scroll-back.
  useEffect(() => {
    if (!isMounted || !items?.length) return;
    const urls = items
      .map((item) => {
        const course = item?.course || item;
        return (
          course?.trailer?.assets?.thumbnail ||
          course?.thumbnail ||
          course?.image ||
          course?.mobileImage ||
          null
        );
      })
      .filter(Boolean);
    preDecodeMany(urls);
  }, [isMounted, items]);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflows = scrollWidth - clientWidth > 2;
    let left = false;
    let right = false;
    if (overflows) {
      if (isRTL) {
        left = Math.abs(scrollLeft) + clientWidth < scrollWidth - 2;
        right = scrollLeft < -2;
      } else {
        left = scrollLeft > 2;
        right = scrollLeft + clientWidth < scrollWidth - 2;
      }
    }
    setCanScrollLeft((p) => (p === left ? p : left));
    setCanScrollRight((p) => (p === right ? p : right));
  }, [isRTL]);

  useEffect(() => {
    if (!isMounted) return;
    const t = setTimeout(updateArrows, 50);
    window.addEventListener("resize", updateArrows);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateArrows);
    };
  }, [isMounted, updateArrows, items.length]);

  const onScrollContainer = useCallback(() => {
    updateArrows();
    if (!isInteracting) setIsInteracting(true);
    clearTimeout(onScrollContainer._t);
    onScrollContainer._t = setTimeout(() => setIsInteracting(false), 150);
  }, [updateArrows, isInteracting]);

  const scrollBy = (direction) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.8;
    const delta = direction === "next" ? amount : -amount;
    scrollRef.current.scrollBy({
      left: isRTL ? -delta : delta,
      behavior: "smooth",
    });
  };

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Persistent-DOM: once a card has been in the virtual window, keep it
  // rendered for the life of the page. This is the FlashList/Discord
  // pattern — no unmount means no re-decode, no flash.
  const seenRef = useRef(new Set());
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!virtualItems.length) return;
    let changed = false;
    for (const vi of virtualItems) {
      if (!seenRef.current.has(vi.index)) {
        seenRef.current.add(vi.index);
        changed = true;
      }
    }
    if (changed) forceRender((n) => n + 1);
  }, [virtualItems]);

  // Merge virtual (currently visible) + seen (previously visible) indices.
  const indicesToRender = useMemo(() => {
    const set = new Set();
    virtualItems.forEach((vi) => set.add(vi.index));
    seenRef.current.forEach((i) => set.add(i));
    return Array.from(set).sort((a, b) => a - b);
  }, [virtualItems]);

  const containerStyle = useMemo(
    () =>
      !isMounted
        ? { minHeight: "380px" }
        : {
            contentVisibility: "auto",
            containIntrinsicSize: "auto 380px",
            contain: "layout paint",
          },
    [isMounted]
  );

  if (!items || items.length === 0) return null;

  return (
    <section ref={sectionRef} className="mt-10 lg:mt-14" style={containerStyle}>
      {!isMounted ? (
        <ShelfSkeleton />
      ) : (
        <>
          <ShelfHeader
            title={title}
            icon={icon}
            onSeeAll={onSeeAll}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            onScroll={scrollBy}
          />
          <div
            ref={scrollRef}
            onScroll={onScrollContainer}
            className="overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: "none",
              overscrollBehaviorX: "contain",
              willChange: isInteracting ? "scroll-position" : "auto",
            }}
          >
            <div
              className="relative min-h-[280px] lg:min-h-[360px]"
              style={{ width: `${totalSize}px` }}
            >
              {indicesToRender.map((index) => {
                const item = items[index];
                if (!item) return null;
                const course = item?.course || item;
                const courseId = course?._id;
                const progress = progressMap?.[courseId] || null;
                return (
                  <div
                    key={courseId || index}
                    className="absolute top-0 left-0 pe-4"
                    style={{
                      width: `${slotWidth}px`,
                      transform: `translateX(${index * slotWidth}px)`,
                    }}
                  >
                    {/* Background pane — covers only the image area with
                        matching rounded-xl radius. Sits behind the card so
                        any flash moment reveals the gradient, not empty. */}
                    <div
                      aria-hidden="true"
                      className="absolute top-0 start-0 end-4 h-[200px] lg:h-[280px] rounded-xl pointer-events-none"
                      style={{
                        background: getPlaceholderGradient(courseId || index),
                      }}
                    />
                    <CourseCard
                      course={course}
                      progress={progress}
                      priority={index < 3}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default Shelf;
